import React, {useState} from 'react';
import {View, Text, TouchableOpacity, Switch} from 'react-native';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import styles, {
  switchTrackColor,
  switchThumbColor,
  infoIconStyle
} from '../../../style/adminTools';
import TooltipBubble from './TooltipBubble';
import useLayoutTier from './useLayoutTier';
import useSettingSave from './useSettingSave';
import HighlightedLabel from './HighlightedLabel';

const SettingToggleRow = ({
  settingKey,
  label,
  tooltip,
  value,
  onSave,
  highlight = false,
  highlightQuery,
  disabled = false,
  disabledReason,
  onSaveStateChange
}) => {
  const phone = useLayoutTier() === 'mobile';
  const [showTooltip, setShowTooltip] = useState(false);
  const {draft, pending, error, save} = useSettingSave({
    settingKey,
    value,
    onSave,
    onSaveStateChange
  });
  const id = `setting-${settingKey}`;
  const blocked = disabled || pending;

  return (
    <View
      style={[
        styles.settingRow,
        phone ? styles.settingRowPhone : null,
        highlight ? styles.settingRowMatch : null
      ]}
      testID={id}
    >
      {showTooltip && tooltip && phone ? (
        <TooltipBubble full text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      <View style={styles.settingRowMain} testID={`${id}-main`}>
        <View style={styles.settingLabelWrap}>
          <HighlightedLabel
            text={label}
            query={highlightQuery}
            style={styles.settingLabel}
            testID={`${id}-label`}
          />
          {tooltip ? (
            <TouchableOpacity
              onPress={() => setShowTooltip(!showTooltip)}
              style={[
                styles.settingInfoButton,
                phone ? styles.settingInfoButtonPhone : null
              ]}
              accessibilityLabel={I18n.t('adminTools.aboutSetting', {label})}
              testID={`${id}-info`}
            >
              <i className="icon-info-circled" style={infoIconStyle} />
            </TouchableOpacity>
          ) : null}
        </View>
        {pending ? (
          <Text style={styles.settingSavingText}>
            {I18n.t('unsavedChanges.saving')}
          </Text>
        ) : null}
        <TouchableOpacity
          style={[styles.switchHit, phone ? styles.switchHitPhone : null]}
          onPress={() => {
            if (!blocked) save(!draft);
          }}
          disabled={blocked}
          accessible={false}
          focusable={false}
          testID={`${id}-switch-hit`}
        >
          <View pointerEvents="none">
            <Switch
              value={!!draft}
              onValueChange={save}
              disabled={blocked}
              accessibilityLabel={label}
              trackColor={switchTrackColor}
              thumbColor={switchThumbColor}
              testID={`${id}-switch`}
            />
          </View>
        </TouchableOpacity>
      </View>
      {showTooltip && tooltip && !phone ? (
        <TooltipBubble text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      {disabled && disabledReason ? (
        <Text style={styles.settingSavingText} testID={`${id}-disabled-reason`}>
          {disabledReason}
        </Text>
      ) : null}
      {error ? (
        <Text style={styles.settingErrorText} testID={`${id}-error`}>
          {error}
        </Text>
      ) : null}
    </View>
  );
};

SettingToggleRow.propTypes = {
  settingKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  highlightQuery: PropTypes.string,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  onSaveStateChange: PropTypes.func
};

export default SettingToggleRow;
